import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Loader from './components/Loader';
import { AuthContext } from './AuthProvider';
import SearchableSelect from './components/SearchableSelect';

function Leads() {
  const { user } = useContext(AuthContext);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [jobRoles, setJobRoles] = useState([]);
  const [editing, setEditing] = useState(null); // lead being edited
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // lead selected for deletion
  const [deleting, setDeleting] = useState(false);
  const isMounted = React.useRef(false);
  const abortControllerRef = React.useRef(null);

  // Advanced Filters State
  const [selectedAssociate, setSelectedAssociate] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedWorkPref, setSelectedWorkPref] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [servicesOptedLogic, setServicesOptedLogic] = useState('and');
  const [associates, setAssociates] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const hasActiveFilters = Boolean(search || selectedStatus || selectedAssociate || selectedRole || selectedWorkPref || selectedServices.length > 0);

  // Export State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFields, setExportFields] = useState({
    name: true,
    email: true,
    apwId: true,
    targetRoleName: true,
    assigned_associate_name: true,
    status: true,
    plan: true,
    yearsExp: true,
    location: true,
    startDate: true,
    endDate: true,
    willingToRelocate: true,
    sponsorship: true,
    workAuthorization: true,
    workPreference: true,
    gender: true,
    alternateJobRolesStr: false,
    country: true,
    excludeCompaniesStr: false,
    clientPreferencesStr: false,
    resumeLink: false,
    resumeUrl: false,
    personalDetailsUrl: false,
    expectedSalaryRange: false,
    githubUrl: false,
    linkedinUrl: false,
    number_of_applications: false,
    google_drive_resume_link: false,
    servicesOptedStr: true,
    created_at: false,
    updated_at: false,
  });

  // Fetch leads (server-side pagination + search + status + custom filters)
  const fetchLeads = async (
    pageParam = page,
    pageSizeParam = pageSize,
    searchParam = search,
    statusParam = selectedStatus,
    associateParam = selectedAssociate,
    roleParam = selectedRole,
    workPrefParam = selectedWorkPref,
    servicesParam = selectedServices,
    logicParam = servicesOptedLogic
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const statusQuery = statusParam ? `&status=${encodeURIComponent(statusParam)}` : '';
      const associateQuery = associateParam ? `&assigned_associate=${encodeURIComponent(associateParam)}` : '';
      const roleQuery = roleParam ? `&targetRoleId=${encodeURIComponent(roleParam)}` : '';
      const workPrefQuery = workPrefParam ? `&workPreference=${encodeURIComponent(workPrefParam)}` : '';
      const servicesQuery = servicesParam.length > 0 
        ? `&services_opted=${encodeURIComponent(servicesParam.join(','))}&services_opted_logic=${encodeURIComponent(logicParam)}` 
        : '';

      const res = await axios.get(
        `/api/v1/leads/?page=${pageParam}&page_size=${pageSizeParam}&search=${encodeURIComponent(searchParam)}${statusQuery}${associateQuery}${roleQuery}${workPrefQuery}${servicesQuery}`, 
        { signal: controller.signal }
      );
      const leadsData = res.data.results || res.data;
      setLeads(leadsData);
      // Infer pagination from DRF standard fields if present
      setHasNext(Boolean(res.data.next));
      setHasPrev(Boolean(res.data.previous));
      if (typeof res.data.total_count === 'number') setTotalCount(res.data.total_count);
      if (typeof res.data.count === 'number') setFilteredCount(res.data.count);
      if (res.data.status_counts) setStatusCounts(res.data.status_counts);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    } catch (e) {
      if (axios.isCancel(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') {
        return; // Ignore canceled request
      }
      console.error('Failed to fetch leads', e.response?.data || e.message);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    } finally {
      if (abortControllerRef.current === controller || abortControllerRef.current === null) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchLeads(1, 12, '', '');
    
    // Fetch active associates
    axios.get('/api/active-ops-users/')
      .then(res => {
        setAssociates(res.data?.opsUsers || []);
      })
      .catch(err => console.error('Failed to fetch associates', err));

    // Fetch job roles
    axios.get('/api/v1/jobroles/?page_size=500')
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setJobRoles(data);
      })
      .catch(err => console.error('Failed to fetch job roles', err));

    const t = setTimeout(() => {
      isMounted.current = true;
    }, 50);
    return () => {
      clearTimeout(t);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Debounced search trigger
  useEffect(() => {
    if (!isMounted.current) return;
    const t = setTimeout(() => {
      setPage(1);
      fetchLeads(1, pageSize, search, selectedStatus, selectedAssociate, selectedRole, selectedWorkPref, selectedServices, servicesOptedLogic);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Instant trigger for other filters
  useEffect(() => {
    if (!isMounted.current) return;
    setPage(1);
    fetchLeads(1, pageSize, search, selectedStatus, selectedAssociate, selectedRole, selectedWorkPref, selectedServices, servicesOptedLogic);
  }, [selectedStatus, selectedAssociate, selectedRole, selectedWorkPref, selectedServices, servicesOptedLogic]);

  const handleClearFilters = () => {
    setSearch('');
    setSelectedStatus('');
    setSelectedAssociate('');
    setSelectedRole('');
    setSelectedWorkPref('');
    setSelectedServices([]);
    setServicesOptedLogic('and');
    setPage(1);
    fetchLeads(1, pageSize, '', '', '', '', '', [], 'and');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const statusQuery = selectedStatus ? `&status=${encodeURIComponent(selectedStatus)}` : '';
      const associateQuery = selectedAssociate ? `&assigned_associate=${encodeURIComponent(selectedAssociate)}` : '';
      const roleQuery = selectedRole ? `&targetRoleId=${encodeURIComponent(selectedRole)}` : '';
      const workPrefQuery = selectedWorkPref ? `&workPreference=${encodeURIComponent(selectedWorkPref)}` : '';
      const servicesQuery = selectedServices.length > 0 
        ? `&services_opted=${encodeURIComponent(selectedServices.join(','))}&services_opted_logic=${encodeURIComponent(servicesOptedLogic)}` 
        : '';
      
      const res = await axios.get(
        `/api/v1/leads/export/?search=${encodeURIComponent(search)}${statusQuery}${associateQuery}${roleQuery}${workPrefQuery}${servicesQuery}`
      );
      
      const leadsToExport = Array.isArray(res.data) ? res.data : (res.data.results || []);
      
      if (leadsToExport.length === 0) {
        alert("No leads found matching the active filters to export.");
        setExporting(false);
        return;
      }

      const selectedKeys = Object.keys(exportFields).filter(key => exportFields[key]);
      const fieldLabels = {
        name: "Name",
        email: "Email",
        apwId: "APW ID",
        targetRoleName: "Target Job Role",
        assigned_associate_name: "Assigned Associate",
        status: "Status",
        plan: "Plan",
        yearsExp: "Experience (years)",
        location: "Location",
        startDate: "Start Date",
        endDate: "End Date",
        willingToRelocate: "Willing to Relocate",
        sponsorship: "Sponsorship Required",
        workAuthorization: "Work Authorization",
        workPreference: "Work Preference",
        gender: "Gender",
        alternateJobRolesStr: "Alternate Job Roles",
        country: "Country",
        excludeCompaniesStr: "Exclude Companies",
        clientPreferencesStr: "Client Preferences",
        resumeLink: "Resume URL",
        resumeUrl: "Uploaded Resume URL",
        personalDetailsUrl: "Uploaded Personal Details URL",
        expectedSalaryRange: "Expected Salary Range",
        githubUrl: "GitHub Profile",
        linkedinUrl: "LinkedIn Profile",
        number_of_applications: "Number of Applications",
        google_drive_resume_link: "Google Drive Resume URL",
        servicesOptedStr: "Services Opted",
        created_at: "Date Created",
        updated_at: "Date Updated",
      };

      const headers = selectedKeys.map(key => fieldLabels[key]);
      const csvRows = [headers.map(h => `"${h.replace(/"/g, '""')}"`).join(',')];

      leadsToExport.forEach(lead => {
        const row = selectedKeys.map(key => {
          let val = lead[key];
          if (typeof val === 'boolean') {
            val = val ? 'Yes' : 'No';
          }
          const strVal = val === null || val === undefined ? '' : String(val);
          return `"${strVal.replace(/"/g, '""')}"`;
        });
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute("href", url);
      link.setAttribute("download", `leads_export_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setShowExportModal(false);
    } catch (e) {
      console.error("Failed to export leads", e);
      alert("Failed to export leads data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // Helper to extract a normalized string role id from a lead object
  const resolveTargetRoleId = (leadObj) => {
    if (!leadObj) return '';
    const extractIdFromObj = (obj) => {
      if (!obj || typeof obj !== 'object') return undefined;
      return obj.id ?? obj.pk ?? obj._id ?? obj.ID ?? obj.Id;
    };
    const nestedId = extractIdFromObj(leadObj.targetRoleId) ?? extractIdFromObj(leadObj.targetRole);
    if (nestedId !== undefined && nestedId !== null) return String(nestedId);
    if (leadObj.target_role_id !== undefined && leadObj.target_role_id !== null) return String(leadObj.target_role_id);
    if (leadObj.targetRoleId !== undefined && leadObj.targetRoleId !== null) return String(leadObj.targetRoleId);
    return '';
  };

  // Open edit modal and prefill form
  const openEdit = useCallback((lead) => {
    setEditing(lead);
    setForm({
      name: lead.name || '',
      email: lead.email || '',
      // normalize to string to match option values
      target_role_id: resolveTargetRoleId(lead),
      yearsExp: lead.yearsExp ?? '',
      location: lead.location || '',
      country: lead.country || 'United States of America',
      status: lead.status || 'In Progress',
      workAuthorization: lead.workAuthorization || 'Citizen'
    });
  }, [setEditing, setForm]);

  // Save changes to API
  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        email: form.email,
        name: form.name,
        // serializer accepts string; normalize to string when present
        target_role_id: form.target_role_id ? String(form.target_role_id) : undefined,
        yearsExp: form.yearsExp === '' ? null : Number(form.yearsExp),
        location: form.location,
        country: form.country,
        status: form.status,
        workAuthorization: form.workAuthorization
      };
      const url = `/api/v1/leads/${editing.id}/`;
      const csrf = document.cookie.split('; ').find(r => r.startsWith('csrftoken='))?.split('=')[1];
      const res = await axios.patch(url, payload, csrf ? { headers: { 'X-CSRFToken': csrf } } : undefined);
      let updated = res.data && Object.keys(res.data).length ? res.data : { ...editing, ...payload };
      if (payload.target_role_id) {
        // Construct minimal nested object using form.target_role_name if server didn't return nested object
        const jr = { id: payload.target_role_id, name: form.target_role_name || editing.targetRoleId?.name || editing.targetRoleName };
        updated = { ...updated, targetRoleId: jr };
      }
      setLeads(ls => ls.map(l => l.id === editing.id ? { ...l, ...updated } : l));
      setEditing(null);
    } catch (e) {
      const detail = e.response?.data || e.message;
      console.error('Failed to save lead', detail);
    } finally {
      setSaving(false);
    }
  };

  // Open delete confirmation
  const openDelete = useCallback((lead) => { setDeleteTarget(lead); }, [setDeleteTarget]);

  // Perform deletion
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const url = `/api/v1/leads/${deleteTarget.id}/`;
      const csrf = document.cookie.split('; ').find(r => r.startsWith('csrftoken='))?.split('=')[1];
      await axios.delete(url, csrf ? { headers: { 'X-CSRFToken': csrf } } : undefined);
      setLeads(ls => ls.filter(l => l.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      console.error('Failed to delete lead', e.response?.data || e.message);
    } finally {
      setDeleting(false);
    }
  };

  // Memoized Lead card to avoid re-rendering whole list when unrelated state changes
  const LeadCard = React.memo(function LeadCard({ lead, onDelete }) {
    const targetRole = lead.targetRoleId?.name || lead.targetRoleName || '\u2014';
    const years = lead.yearsExp != null ? `${lead.yearsExp} year${lead.yearsExp === 1 ? '' : 's'}` : '\u2014';
    const location = lead.location || '\u2014';
    const plan = lead.plan || 'Standard';
    const startDate = lead.startDate || (lead.created_at ? lead.created_at.split('T')[0] : '\u2014');
    const endDate = lead.endDate || '\u2014';
    const status = (lead.status || 'New').trim();
    const associateName = lead.assigned_associate?.name || 'Unassigned';
    return (
      <div key={lead.id} className="group relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6 flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">{lead.name}</h3>
            <p className="text-gray-600 text-sm mt-1">{lead.email}</p>
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 shadow-inner">{status}</span>
        </div>
        <dl className="text-sm flex-1 space-y-2">
          <div className="grid grid-cols-5">
            <dt className="col-span-2 font-medium text-gray-500">Target Role:</dt>
            <dd className="col-span-3 font-medium text-gray-900 truncate" title={targetRole}>{targetRole}</dd>
          </div>
          <div className="grid grid-cols-5">
            <dt className="col-span-2 font-medium text-gray-500">Experience:</dt>
            <dd className="col-span-3 text-gray-900">{years}</dd>
          </div>
          <div className="grid grid-cols-5">
            <dt className="col-span-2 font-medium text-gray-500">Location:</dt>
            <dd className="col-span-3 text-gray-900">{location}</dd>
          </div>
          <div className="grid grid-cols-5">
            <dt className="col-span-2 font-medium text-gray-500">Plan:</dt>
            <dd className="col-span-3 text-gray-900">{plan}</dd>
          </div>
          <div className="grid grid-cols-5">
            <dt className="col-span-2 font-medium text-gray-500">Associate:</dt>
            <dd className="col-span-3 text-gray-900 truncate" title={associateName}>{associateName}</dd>
          </div>
          <div className="grid grid-cols-5">
            <dt className="col-span-2 font-medium text-gray-500">Start Date:</dt>
            <dd className="col-span-3 text-gray-900">{startDate}</dd>
          </div>
          <div className="grid grid-cols-5">
            <dt className="col-span-2 font-medium text-gray-500">End Date:</dt>
            <dd className="col-span-3 text-gray-900">{endDate}</dd>
          </div>
        </dl>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={`/leads/${lead.id}`} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <span role="img" aria-label="view">👁️</span> View Full Details
          </Link>
          {user?.role_name !== 'Associate admin' && (
            <button type="button" onClick={() => onDelete(lead)} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500">
              <span role="img" aria-label="delete">🗑️</span> Delete
            </button>
          )}
        </div>
      </div>
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white px-4 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Row 1: Title and Status Filter Badges */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Leads</h1>
            <div className="flex flex-wrap gap-2 ml-4">
              <button
                type="button"
                onClick={() => setSelectedStatus('')}
                className={`text-sm px-3 py-1 rounded-full font-medium transition-all duration-200 cursor-pointer ${
                  selectedStatus === ''
                    ? 'bg-indigo-600 text-white shadow-sm ring-2 ring-indigo-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                }`}
              >
                Total: {totalCount}
              </button>
              {Object.entries(statusCounts).map(([k, v]) => {
                const label = k.replace(/_/g, ' ').toUpperCase() === k ? k.split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') : k;
                const isSelected = selectedStatus === label;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setSelectedStatus(isSelected ? '' : label)}
                    className={`text-sm px-3 py-1 rounded-full font-medium transition-all duration-200 cursor-pointer ${
                      isSelected
                        ? 'bg-indigo-600 text-white shadow-sm ring-2 ring-indigo-300'
                        : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-900'
                    }`}
                  >
                    {label}: {v}
                  </button>
                );
              })}
            </div>
          </div>
          <Link to="/create-lead" className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Create Lead
          </Link>
        </div>

        {/* Row 2: Search Input, Filters Toggle, and Create Action */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name, email, location..."
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer ${
                showAdvancedFilters
                  ? 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 8.293A1 1 0 013 7.586V4z" />
              </svg>
              {showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
            </button>
            <button
              type="button"
              onClick={() => setShowExportModal(true)}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
            >
              <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* Collapsible Advanced Filters Pane */}
        {showAdvancedFilters && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-8 transition-all duration-300">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Associate Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Associate</label>
                <SearchableSelect
                  options={[
                    { value: 'unassigned', label: 'Unassigned / NA' },
                    ...associates.map((assoc) => ({
                      value: String(assoc.id),
                      label: `${assoc.name} (${assoc.email})`
                    }))
                  ]}
                  value={selectedAssociate}
                  onChange={setSelectedAssociate}
                  placeholder="All Associates"
                />
              </div>

              {/* Job Role Searchable Select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Job Role</label>
                <SearchableSelect
                  options={jobRoles.map(role => ({ value: role.id, label: role.name }))}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  placeholder="Select job role..."
                />
              </div>

              {/* Work Preference Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Preference</label>
                <select
                  value={selectedWorkPref}
                  onChange={(e) => setSelectedWorkPref(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Preferences</option>
                  {['Remote', 'Hybrid', 'On-site'].map(pref => (
                    <option key={pref} value={pref}>{pref}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Services Opted Filter Section */}
            <div className="mt-6 border-t border-gray-100 pt-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Services Opted</span>
                  <div className="relative group flex items-center">
                    <span className="text-gray-400 hover:text-gray-600 cursor-help text-xs bg-gray-100 border border-gray-200 rounded-full w-5 h-5 flex items-center justify-center font-bold font-serif">i</span>
                    <div className="absolute left-6 bottom-0 hidden group-hover:block bg-gray-900 text-white text-xs rounded p-3 w-64 shadow-xl z-50 leading-relaxed">
                      <p className="font-semibold mb-1">Filter Logic Options:</p>
                      <p className="mb-2"><strong>Match Any (OR):</strong> Leads having *at least one* of the checked services.</p>
                      <p><strong>Match All (AND):</strong> Leads having *all* of the checked services together.</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">Filter Logic:</span>
                  <div className="inline-flex rounded-md shadow-sm">
                    <button
                      type="button"
                      onClick={() => setServicesOptedLogic('or')}
                      className={`px-3 py-1 text-xs font-semibold rounded-l-md border cursor-pointer ${
                        servicesOptedLogic === 'or'
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Match Any (OR)
                    </button>
                    <button
                      type="button"
                      onClick={() => setServicesOptedLogic('and')}
                      className={`px-3 py-1 text-xs font-semibold rounded-r-md border-t border-b border-r cursor-pointer ${
                        servicesOptedLogic === 'and'
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Match All (AND)
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {['applications', 'resume', 'portfolio', 'linkedin', 'github', 'courses', 'badge', 'job-links'].map((service) => {
                  const isChecked = selectedServices.includes(service);
                  return (
                    <button
                      key={service}
                      type="button"
                      onClick={() => {
                        if (isChecked) {
                          setSelectedServices(selectedServices.filter(s => s !== service));
                        } else {
                          setSelectedServices([...selectedServices, service]);
                        }
                      }}
                      className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all cursor-pointer ${
                        isChecked
                          ? 'bg-indigo-50 text-indigo-700 border-indigo-300 shadow-sm ring-1 ring-indigo-300'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {service.replace('-', ' ').toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Actions Row */}
            <div className="mt-6 border-t border-gray-100 pt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                {hasActiveFilters && (
                  <p className="text-sm text-gray-500">
                    Selected filters match <strong>{filteredCount}</strong> out of <strong>{totalCount}</strong> total leads.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Pagination Status and Page Size Selector */}
        <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="text-xs text-gray-500 font-medium">
            Showing {leads.length > 0 ? (page - 1) * pageSize + 1 : 0}-{Math.min(filteredCount, page * pageSize)} of {filteredCount} records
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="page-size" className="text-xs font-bold text-gray-500 uppercase">Rows per page:</label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);
                setPage(1);
                fetchLeads(1, newSize, search, selectedStatus, selectedAssociate, selectedRole, selectedWorkPref, selectedServices, servicesOptedLogic);
              }}
              className="border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-2 py-1 rounded-lg text-xs bg-white outline-none transition-all font-semibold text-slate-700 cursor-pointer"
            >
              {[6, 12, 24, 48, 96].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            <div className="col-span-full">
              <Loader showSkeleton={false} />
            </div>
          ) : leads.length === 0 ? (
            <div className="col-span-full text-center text-gray-500">No leads found.</div>
          ) : (
            leads.map(lead => (
              <LeadCard key={lead.id} lead={lead} onDelete={openDelete} />
            ))
          )}
        </div>
        {/* Pagination Controls */}
        {!loading && leads.length > 0 && (
          <div className="flex justify-center items-center gap-2 mt-10">
            <button
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:pointer-events-none active:scale-95 cursor-pointer"
              disabled={!hasPrev || loading}
              onClick={() => {
                const prevPage = Math.max(1, page - 1);
                setPage(prevPage);
                fetchLeads(prevPage, pageSize, search, selectedStatus, selectedAssociate, selectedRole, selectedWorkPref, selectedServices, servicesOptedLogic);
              }}
            >
              Prev
            </button>
            {Array.from({ length: Math.max(1, Math.ceil(filteredCount / pageSize)) }, (_, i) => i + 1).slice(
              Math.max(0, page - 3),
              Math.max(0, page - 3) + 5
            ).map(n => (
              <button
                key={n}
                className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold transition-all border cursor-pointer ${
                  n === page 
                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm' 
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => {
                  setPage(n);
                  fetchLeads(n, pageSize, search, selectedStatus, selectedAssociate, selectedRole, selectedWorkPref, selectedServices, servicesOptedLogic);
                }}
              >
                {n}
              </button>
            ))}
            <button
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:pointer-events-none active:scale-95 cursor-pointer"
              disabled={!hasNext || loading}
              onClick={() => {
                const nextPage = Math.min(Math.max(1, Math.ceil(filteredCount / pageSize)), page + 1);
                setPage(nextPage);
                fetchLeads(nextPage, pageSize, search, selectedStatus, selectedAssociate, selectedRole, selectedWorkPref, selectedServices, servicesOptedLogic);
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
      {editing && (
        <EditModal
          lead={editing}
          form={form}
          jobRoles={jobRoles}
          saving={saving}
          onChange={e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          lead={deleteTarget}
          deleting={deleting}
          onCancel={() => !deleting && setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
      {showExportModal && (
        <ExportModal
          fields={exportFields}
          setFields={setExportFields}
          onClose={() => setShowExportModal(false)}
          onConfirm={handleExport}
          exporting={exporting}
          filteredCount={filteredCount}
          totalCount={totalCount}
          hasActiveFilters={hasActiveFilters}
        />
      )}
    </div>
  );
}

function EditModal({ lead, form, jobRoles, onChange, onClose, onSave, saving }) {
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center p-4 bg-gray-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white w-full max-w-5xl rounded-2xl shadow-xl border border-gray-200 relative">
        <form onSubmit={(e) => { e.preventDefault(); onSave(); }} className="p-6 space-y-6 w-full">
          <h2 className="text-xl font-semibold text-gray-900">{lead.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700">Name *</label>
              <input name="name" value={form.name || ''} onChange={onChange} required className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Email *</label>
              <input name="email" type="email" value={form.email || ''} disabled className="mt-1 w-full rounded-md border border-gray-200 bg-gray-100 px-3 py-2 cursor-not-allowed" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Target Role *</label>
              {(() => {
                const resolvedId = String(form.target_role_id || '');
                const hasOption = Array.isArray(jobRoles) && jobRoles.some(r => String(r.id) === resolvedId);
                const syntheticLabel = (lead && (lead.targetRoleId?.name || lead.targetRoleName || lead.targetRole)) || null;
                return (
                  <select name="target_role_id" value={form.target_role_id || ''} onChange={onChange} required className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">Select a role</option>
                    {!hasOption && resolvedId ? <option key={`synthetic-${resolvedId}`} value={resolvedId}>{syntheticLabel || `Role ${resolvedId}`}</option> : null}
                    {jobRoles.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                  </select>
                );
              })()}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Experience (years) *</label>
              <input name="yearsExp" type="number" min="0" value={form.yearsExp || ''} onChange={onChange} required className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Location *</label>
              <input name="location" value={form.location || ''} onChange={onChange} required className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Country</label>
              <select name="country" value={form.country || 'United States of America'} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                {['United States of America', 'Canada', 'United Kingdom', 'Ireland', 'Australia', 'Germany', 'France'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Status</label>
              <select name="status" value={form.status || 'In Progress'} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                {(() => {
                  const standardStatuses = ['New', 'Active', 'In Progress', 'Paused', 'Completed'];
                  const currentStatus = form.status;
                  const statuses = [...standardStatuses];
                  if (currentStatus && !standardStatuses.includes(currentStatus)) {
                    statuses.push(currentStatus);
                  }
                  return statuses.map(s => <option key={s} value={s}>{s}</option>);
                })()}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Work Authorization</label>
              <select name="workAuthorization" value={form.workAuthorization || 'Citizen'} onChange={onChange} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                {['Citizen', 'Green Card', 'H1B', 'F1', 'Post-Study Work Visa', 'H4EAD', 'Other'].map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2 rounded-md bg-indigo-600 text-white font-medium shadow hover:bg-indigo-700 disabled:opacity-50">{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Leads;

// Simple delete confirmation modal
function DeleteModal({ lead, deleting, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl border border-gray-200 p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Delete Lead</h2>
          <p className="mt-2 text-sm text-gray-600">Are you sure you want to permanently delete <span className="font-medium">{lead.name}</span>? This action cannot be undone.</p>
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" disabled={deleting} onClick={onCancel} className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button type="button" disabled={deleting} onClick={onConfirm} className="px-4 py-2 rounded-md bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50">{deleting ? 'Deleting...' : 'Delete'}</button>
        </div>
      </div>
    </div>
  );
}

function ExportModal({ fields, setFields, onClose, onConfirm, exporting, filteredCount, totalCount, hasActiveFilters }) {
  const fieldLabels = {
    name: "Name",
    email: "Email",
    apwId: "APW ID",
    targetRoleName: "Target Job Role",
    assigned_associate_name: "Assigned Associate",
    status: "Status",
    plan: "Plan",
    yearsExp: "Experience (years)",
    location: "Location",
    startDate: "Start Date",
    endDate: "End Date",
    willingToRelocate: "Willing to Relocate",
    sponsorship: "Sponsorship Required",
    workAuthorization: "Work Authorization",
    workPreference: "Work Preference",
    gender: "Gender",
    alternateJobRolesStr: "Alternate Job Roles",
    country: "Country",
    excludeCompaniesStr: "Exclude Companies",
    clientPreferencesStr: "Client Preferences",
    resumeLink: "Resume URL",
    resumeUrl: "Uploaded Resume URL",
    personalDetailsUrl: "Uploaded Personal Details URL",
    expectedSalaryRange: "Expected Salary Range",
    githubUrl: "GitHub Profile",
    linkedinUrl: "LinkedIn Profile",
    number_of_applications: "Number of Applications",
    google_drive_resume_link: "Google Drive Resume URL",
    servicesOptedStr: "Services Opted",
    created_at: "Date Created",
    updated_at: "Date Updated",
  };

  const handleToggleAll = (value) => {
    const updated = {};
    Object.keys(fields).forEach(key => {
      updated[key] = value;
    });
    setFields(updated);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-xl border border-gray-200 flex flex-col max-h-[85vh]">
        <div className="p-6 border-b border-gray-150">
          <h2 className="text-xl font-semibold text-gray-900">Export Leads Data</h2>
          <p className="mt-1 text-sm text-gray-500">
            {hasActiveFilters ? (
              <>Selected filters match <strong>{filteredCount}</strong> out of <strong>{totalCount}</strong> total leads.</>
            ) : (
              <>Exporting all <strong>{totalCount}</strong> leads.</>
            )}
          </p>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Available Columns</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleToggleAll(true)}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 focus:outline-none cursor-pointer"
              >
                Select All
              </button>
              <span className="text-gray-300 text-xs">|</span>
              <button
                type="button"
                onClick={() => handleToggleAll(false)}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 focus:outline-none cursor-pointer"
              >
                Deselect All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(fieldLabels).map(([key, label]) => (
              <label key={key} className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={fields[key]}
                  onChange={(e) => setFields(prev => ({ ...prev, [key]: e.target.checked }))}
                  className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700 leading-tight">{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-gray-150 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            disabled={exporting}
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 cursor-pointer text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={exporting || !Object.values(fields).some(Boolean)}
            onClick={onConfirm}
            className="px-5 py-2 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 cursor-pointer text-sm flex items-center gap-2 shadow"
          >
            {exporting ? 'Exporting...' : `Download CSV (${hasActiveFilters ? `${filteredCount} of ${totalCount}` : filteredCount} leads)`}
          </button>
        </div>
      </div>
    </div>
  );
}
